import firebase from 'firebase/app';
import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import isNil from 'lodash/isNil';
import { useCurrentUserProfile } from '../app/hooks/useCurrentUserProfile';
import { useUserIsAuthenticated } from '../app/hooks/useUserIsAuthenticated';
import { RemoteConfigKey } from '../firebase/remoteConfig';
import { useStoreActions, useStoreState } from '../models/hooks';
import { AdministrationLayout } from '../modules/administration/AdministrationLayout';
import { RecruiterCalendarView } from '../modules/administration/recruiterCalendar/RecruiterCalendarView';
import { TeamView } from '../modules/administration/team/TeamView';
import { TopCandidateHome } from '../modules/administration/topCandidates/TopCandidateHome';
import { AnalyticsView } from '../modules/analytics/AnalyticsView';
import { NewAnalyticsView } from '../modules/analytics/NewAnalyticsView';
import { CalendarView } from '../modules/calendar/CalendarView';
// import { DashboardHome } from '../modules/dashboard/DashboardHome';
import OfferAndHireProcessView from '../modules/dashboard/offerAndHireProcessOverview/OfferAndHireProcessView';
import { EmailInvitationView } from '../modules/emailInvitation/EmailInvitationView';
import { JobPositionView } from '../modules/jobPosition/JobPositionView';
import { MeetView } from '../modules/meet/MeetView';
import { CandidateMessagingView } from '../modules/messaging/CandidateMessagingView';
import { InterviewMessagingView } from '../modules/messaging/InterviewMessagingView';
import { MessagingView } from '../modules/messaging/MessagingView';
import { SupportLayout } from '../modules/support/SupportLayout';
import UnsubscribeView from '../modules/unsubscribe/UnSubscribeView';
import { isCandidatePage, isCandidatesPage } from '../utils/candidatesUtils';
import PrivateRoute from './PrivateRoute';
import {
  PATH_ADMINISTRATION,
  PATH_ADMINISTRATION_TABS,
  PATH_ANALYTICS,
  PATH_CALENDAR,
  PATH_CANDIDATE,
  PATH_DASHBOARD,
  PATH_EMAIL_INVITATION,
  PATH_GUEST_MEET,
  PATH_INTERVIEW_MESSAGING_TABS,
  PATH_LOGIN,
  PATH_MAIN,
  PATH_MEET,
  PATH_MESSAGING_TABS,
  PATH_OFFER_HIRE_PROCESS,
  PATH_POSITION,
  PATH_MANAGEMENT_OVERVIEW,
  PATH_POSITIONS,
  PATH_PROTOTYPE,
  PATH_RECRUITER_CALENDAR,
  PATH_ROOT,
  PATH_SUPPORT,
  PATH_TEAM,
  PATH_UNSUBSCRIBE,
  PATH_SUCCESSFUL,
  PATH_SMB_BUSINESS_MANAGEMENT,
  PATH_SMB_POSITION_MANAGEMENT,
  PATH_POSITION_CANDIDATE,
  PATH_DASHBOARD_REQUISITION,
  PATH_SMB_LOGIN,
  // PATH_MAIN_NEW,
  // PATH_MESSAGING_TABS_NEW,
  PATH_HIRE_FORM_COPY,
  PATH_HIRE_FORM_CREATE,
  PATH_HIRE_FORM_EDIT,
  PATH_CANDIDATE_SEARCH,
  PATH_FORBIDDEN,
  PATH_QA_LOGIN,
  PATH_QUESTIONNAIRE_TEMPLATE_CREATE,
  PATH_QUESTIONNAIRE_TEMPLATE_COPY,
  PATH_QUESTIONNAIRE_TEMPLATE_EDIT,
} from './constants';
import { ManagementOverviewView } from '../modules/managementOverview/ManagementOverviewView';
import { PaymentSuccessfulView } from '../modules/paymentSuccess/PaymentSuccessView';
import { SMBLoginView } from '../modules/auth/login/SMBLoginView';
import { SmbPositionManagementView } from '../modules/smbPositionManagement/SmbPositionManagementView/SmbPositionManagementView';
import { SmbBusinessManagementView } from '../modules/smbPositionManagement/SmbPositionManagementView/SmbBusinessManagementView';
import { DashboardRequisition } from '../modules/dashboard/DashboardRequisition';
import { NewMessagingView } from '../modules/messaging/NewMessagingView';
import { CandidateSearchView } from '../modules/candidateSearch/CandidateSearchView';
import App from '../modules/administration/dnd/App';
import Forbidden from '../modules/Forbidden';
import { QALoginView } from '../modules/auth/login/LoginView-QA-Env';
import 'firebase/analytics';
import { Dashboard } from '../modules/newDashboard/Dashboard';
import { QuestionnaireFormBuilder } from '../modules/administration/questionnaireTemplate/formBuilder/QuestionnaireFormBuilder';

const Routes = (): JSX.Element => {
  /* istanbul ignore next */
  const loginPath = process.env.REACT_APP_ENV === 'qa' ? PATH_QA_LOGIN : PATH_LOGIN;
  /* istanbul ignore next */
  const LoginComponent = process.env.REACT_APP_ENV === 'qa' ? QALoginView : SMBLoginView;

  const location = useLocation();
  const isAuthenticated = useUserIsAuthenticated();
  const { currentUserProfile } = useCurrentUserProfile();

  const { loadCandidate } = useStoreActions((actions) => actions.candidate);
  const minutesBeforeInactivity = useStoreState((state) => state.remoteConfig[RemoteConfigKey.MINUTES_BEFORE_INACTIVITY]);
  const superOwners = useStoreState((s) => s.app.accounts?.superOwners);
  const userId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const currentAccountId = useStoreState((state) => state.app.accounts?.id);

  /* istanbul ignore next */
  useEffect(() => {
    // Scroll to bottom of the page if on candidate-related pages
    if (isCandidatePage() || isCandidatesPage()) {
      window.scrollTo(0, document.body.scrollHeight);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserProfile, loadCandidate, minutesBeforeInactivity, location, currentAccountId]);

  useEffect(() => {
    // istanbul ignore else
    if (location.pathname) {
      firebase.analytics().logEvent('change_route', { pathname: location.pathname });
    }
  }, [location.pathname]);

  const isNewAnalyticEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_NEW_ANALYTIC_VIEW];
  });

  const isSuperAdmin = (): boolean => {
    if (!superOwners || !userId) {
      return false;
    }
    return superOwners?.includes(userId);
  };

  const isAdmin = !isNil(appOwnersId) && !isNil(userId) && appOwnersId?.includes(userId);

  return (
    <Switch location={location} key={location.pathname}>
      <Redirect exact from={PATH_ROOT} to={loginPath} />
      <Route exact path={loginPath} component={LoginComponent} />
      <Route exact path={PATH_SMB_LOGIN} component={SMBLoginView} />
      <Route exact path={PATH_GUEST_MEET} component={MeetView} />
      <Route exact path={PATH_TEAM} component={TeamView} />
      <Route exact path={PATH_MEET} component={MeetView} />
      <Route exact path={PATH_SUPPORT} component={SupportLayout} />
      <Route exact path={PATH_EMAIL_INVITATION} component={EmailInvitationView} />
      <Route exact path={PATH_UNSUBSCRIBE} component={UnsubscribeView} />
      <PrivateRoute exact path={PATH_ADMINISTRATION} userIsAuth={isAuthenticated} component={AdministrationLayout} />
      <PrivateRoute exact path={PATH_POSITION} userIsAuth={isAuthenticated} component={TopCandidateHome} />
      <PrivateRoute exact path={PATH_ADMINISTRATION_TABS} userIsAuth={isAuthenticated} component={AdministrationLayout} />
      <PrivateRoute
        exact
        path={PATH_ANALYTICS}
        userIsAuth={isAuthenticated}
        component={isNewAnalyticEnabled ? NewAnalyticsView : AnalyticsView}
      />
      <PrivateRoute exact path={PATH_PROTOTYPE} userIsAuth={isAuthenticated} component={NewAnalyticsView} />
      {/* <PrivateRoute exact path={PATH_MAIN_NEW} userIsAuth={isAuthenticated} component={MessagingView} /> */}
      <PrivateRoute exact path={PATH_MAIN} userIsAuth={isAuthenticated} component={NewMessagingView} />
      {/* <PrivateRoute exact path={PATH_MESSAGING_TABS_NEW} userIsAuth={isAuthenticated} component={MessagingView} /> */}
      <PrivateRoute exact path={PATH_MESSAGING_TABS} userIsAuth={isAuthenticated} component={NewMessagingView} />

      {/* <PrivateRoute exact path={PATH_MAIN_NEW} userIsAuth={isAuthenticated} component={NewMessagingView} />
      <PrivateRoute exact path={PATH_MAIN} userIsAuth={isAuthenticated} component={MessagingView} />
      <PrivateRoute exact path={PATH_MESSAGING_TABS_NEW} userIsAuth={isAuthenticated} component={NewMessagingView} />
      <PrivateRoute exact path={PATH_MESSAGING_TABS} userIsAuth={isAuthenticated} component={MessagingView} /> */}

      <PrivateRoute exact path={PATH_CANDIDATE} userIsAuth={isAuthenticated} component={CandidateMessagingView} />
      <PrivateRoute exact path={PATH_POSITION_CANDIDATE} userIsAuth={isAuthenticated} component={NewMessagingView} />
      <PrivateRoute exact path={PATH_INTERVIEW_MESSAGING_TABS} userIsAuth={isAuthenticated} component={InterviewMessagingView} />
      <PrivateRoute exact path={PATH_CALENDAR} userIsAuth={isAuthenticated} component={CalendarView} />
      <PrivateRoute exact path={PATH_RECRUITER_CALENDAR} userIsAuth={isAuthenticated} component={RecruiterCalendarView} />
      <PrivateRoute exact path={PATH_POSITIONS} userIsAuth={isAuthenticated} component={JobPositionView} />
      <PrivateRoute exact path={PATH_DASHBOARD} userIsAuth={isAuthenticated} component={Dashboard} />
      <PrivateRoute exact path={PATH_DASHBOARD_REQUISITION} userIsAuth={isAuthenticated} component={DashboardRequisition} />

      <PrivateRoute
        exact
        path={PATH_SMB_BUSINESS_MANAGEMENT}
        userIsAuth={isAuthenticated}
        component={SmbBusinessManagementView}
      />

      <PrivateRoute
        exact
        path={PATH_SMB_POSITION_MANAGEMENT}
        userIsAuth={isAuthenticated}
        component={SmbPositionManagementView}
      />

      <PrivateRoute
        exact
        path={PATH_MANAGEMENT_OVERVIEW}
        userIsAuth={isAuthenticated}
        component={isSuperAdmin() || isAdmin ? ManagementOverviewView : MessagingView}
      />
      <PrivateRoute exact path={PATH_OFFER_HIRE_PROCESS} userIsAuth={isAuthenticated} component={OfferAndHireProcessView} />
      <PrivateRoute exact path={PATH_SUCCESSFUL} userIsAuth={isAuthenticated} component={PaymentSuccessfulView} />
      <PrivateRoute exact path={PATH_HIRE_FORM_CREATE} userIsAuth={isAuthenticated} component={App} />
      <PrivateRoute exact path={PATH_HIRE_FORM_EDIT} userIsAuth={isAuthenticated} component={App} />
      <PrivateRoute exact path={PATH_HIRE_FORM_COPY} userIsAuth={isAuthenticated} component={App} />
      <PrivateRoute exact path={PATH_CANDIDATE_SEARCH} userIsAuth={isAuthenticated} component={CandidateSearchView} />
      <PrivateRoute
        exact
        path={PATH_QUESTIONNAIRE_TEMPLATE_CREATE}
        userIsAuth={isAuthenticated}
        component={QuestionnaireFormBuilder}
      />
      <PrivateRoute
        exact
        path={PATH_QUESTIONNAIRE_TEMPLATE_EDIT}
        userIsAuth={isAuthenticated}
        component={QuestionnaireFormBuilder}
      />
      <PrivateRoute
        exact
        path={PATH_QUESTIONNAIRE_TEMPLATE_COPY}
        userIsAuth={isAuthenticated}
        component={QuestionnaireFormBuilder}
      />
      <Route path={PATH_FORBIDDEN} component={Forbidden} />
      {/* <PrivateRoute exact path={'/dnd'} userIsAuth={isAuthenticated} component={App} /> */}
      <Redirect from="*" to={isAuthenticated ? PATH_MAIN : loginPath} />
    </Switch>
  );
};

export default Routes;
